import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';
import axios from 'axios';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

function SignupsDashboard({ token, onLogout, me }) {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  // Ticket-launch blast state
  const [preview, setPreview] = useState(null);
  const [showBlastModal, setShowBlastModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const isSuperadmin = me?.role === 'superadmin';

  const loadSignups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/bonfire-signups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSignups(data);
      // Also refresh the pending-count preview if we're superadmin.
      if (isSuperadmin) {
        try {
          const { data: p } = await axios.get(`${API}/admin/bonfire-signups/ticket-launch-preview`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPreview(p);
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        setError('Failed to load signups.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, isSuperadmin]);

  useEffect(() => {
    loadSignups();
  }, [loadSignups]);

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/bonfire-signups.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      link.setAttribute('download', match ? match[1].trim() : 'bonfire-signups.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        setError('Failed to download CSV.');
      }
    }
  };

  const handleSendBlast = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/bonfire-signups/notify-ticket-launch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBlastResult(data);
      await loadSignups();
    } catch (err) {
      setBlastResult({
        sent: 0, skipped: 0, failed: 1,
        errors: [err.response?.data?.detail || err.message || 'Failed to send'],
      });
    } finally {
      setSending(false);
    }
  };

  const filtered = signups.filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
  });

  const activeCount = signups.filter((s) => !s.unsubscribed_at).length;
  const unsubCount = signups.length - activeCount;

  return (
    <div className="bg-[#F4F4F5] min-h-screen py-16 px-4 sm:px-6 lg:px-8" data-testid="admin-signups-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">
              Admin Dashboard
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">Bonfire Night 2026 — Launch List</h1>
            <p className="text-sm text-[#52525B] mt-2" data-testid="admin-signup-counts">
              {activeCount} active &middot; {unsubCount} unsubscribed &middot; {signups.length} total
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={loadSignups}
              data-testid="admin-refresh-button"
              className="px-4 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm transition-colors"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={handleDownloadCsv}
              disabled={signups.length === 0}
              data-testid="admin-download-csv-button"
              className="px-4 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs transition-colors"
            >
              Download CSV
            </button>
            {isSuperadmin && (
              <button
                type="button"
                onClick={() => { setBlastResult(null); setShowBlastModal(true); }}
                disabled={!preview || preview.pending === 0}
                data-testid="admin-ticket-blast-button"
                className="px-4 py-2 bg-[#D62F2F] hover:bg-[#b02020] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider text-xs transition-colors"
                title={preview?.pending === 0 ? 'Everyone has already been notified' : ''}
              >
                📧 Email {preview?.pending ?? '…'} pending signups
              </button>
            )}
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold text-sm transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mb-6">
          <Input
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-search-input"
            className="bg-white max-w-md"
          />
        </div>

        {error && (
          <div data-testid="admin-error" className="p-4 mb-6 bg-red-50 border-l-4 border-red-500 text-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white border border-zinc-200 overflow-hidden">
          {renderTableBody({ loading, signups, filtered })}
        </div>

        {/* Ticket-launch blast confirmation modal */}
        {showBlastModal && (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            data-testid="ticket-blast-modal"
            onClick={() => !sending && !blastResult && setShowBlastModal(false)}
          >
            <div
              className="bg-white max-w-lg w-full p-6 sm:p-8 border-l-4 border-[#F0A500]"
              onClick={(e) => e.stopPropagation()}
            >
              {!blastResult ? (
                <>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-3">Send ticket-launch email?</h2>
                  <p className="text-sm text-[#52525B] mb-4 leading-relaxed">
                    This will send a one-off <strong>"Tickets are on sale"</strong> email to <strong className="text-[#0A0A0A]">{preview?.pending ?? 0} subscribers</strong> who haven't been notified yet.
                  </p>
                  <div className="bg-[#F4F4F5] border-l-4 border-[#F0A500] p-4 mb-5 text-xs text-[#52525B] space-y-1">
                    <div><strong>Pending (will receive):</strong> {preview?.pending ?? 0}</div>
                    <div><strong>Already notified (will skip):</strong> {preview?.already_notified ?? 0}</div>
                    <div><strong>Unsubscribed (will skip):</strong> {preview?.unsubscribed ?? 0}</div>
                  </div>
                  <details className="mb-5 text-xs">
                    <summary className="cursor-pointer font-bold text-[#0A0A0A] uppercase tracking-widest">
                      Preview email content ▸
                    </summary>
                    <div className="mt-3 bg-zinc-50 border border-zinc-200 p-3 space-y-2">
                      <div><strong>Subject:</strong> 🎆 Bonfire Night tickets are on sale — Glossop Round Table</div>
                      <div><strong>Includes:</strong> TicketSource link, advance prices, cashless gate warning, one-click unsubscribe</div>
                    </div>
                  </details>
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowBlastModal(false)}
                      disabled={sending}
                      data-testid="ticket-blast-cancel"
                      className="px-5 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSendBlast}
                      disabled={sending || (preview?.pending ?? 0) === 0}
                      data-testid="ticket-blast-confirm"
                      className="px-5 py-2 bg-[#D62F2F] hover:bg-[#b02020] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs"
                    >
                      {sending ? 'Sending…' : `Yes, send to ${preview?.pending ?? 0} people`}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-3">
                    {blastResult.failed > 0 ? '⚠️ Completed with issues' : '✅ Emails sent'}
                  </h2>
                  <div className="bg-[#F4F4F5] border-l-4 border-[#F0A500] p-4 mb-5 text-sm text-[#52525B] space-y-1">
                    <div><strong>Sent successfully:</strong> {blastResult.sent}</div>
                    {blastResult.failed > 0 && <div><strong>Failed:</strong> {blastResult.failed}</div>}
                  </div>
                  {blastResult.errors && blastResult.errors.length > 0 && (
                    <details className="mb-5">
                      <summary className="cursor-pointer text-xs font-bold text-[#D62F2F] uppercase tracking-widest">
                        Failures ▸
                      </summary>
                      <ul className="mt-2 text-xs text-[#52525B] space-y-1 list-disc list-inside">
                        {blastResult.errors.map((e, i) => <li key={i} className="break-all">{e}</li>)}
                      </ul>
                    </details>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setShowBlastModal(false); setBlastResult(null); }}
                      data-testid="ticket-blast-close"
                      className="px-5 py-2 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-xs"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function renderTableBody({ loading, signups, filtered }) {
  if (loading) {
    return <div className="p-12 text-center text-[#52525B]" data-testid="admin-loading">Loading…</div>;
  }
  if (filtered.length === 0) {
    const message = signups.length === 0
      ? "No signups yet. Once people start signing up on the Bonfire page, they'll appear here."
      : 'No matches for your search.';
    return <div className="p-12 text-center text-[#52525B]" data-testid="admin-empty-state">{message}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-signups-table">
        <thead className="bg-[#0A0A0A] text-white">
          <tr>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">#</th>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">Name</th>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">Email</th>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">Status</th>
            <th className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs">Submitted</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((s, i) => <SignupRow key={s.id} signup={s} index={i} />)}
        </tbody>
      </table>
    </div>
  );
}

function SignupRow({ signup, index }) {
  const isUnsub = !!signup.unsubscribed_at;
  const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-zinc-50';
  const nameClass = `px-4 py-3 font-medium ${isUnsub ? 'text-zinc-400 line-through' : 'text-[#0A0A0A]'}`;
  const emailClass = `hover:underline ${isUnsub ? 'text-zinc-400 line-through' : 'text-[#F0A500]'}`;
  return (
    <tr className={rowBg}>
      <td className="px-4 py-3 text-[#52525B]">{index + 1}</td>
      <td className={nameClass}>{signup.name}</td>
      <td className="px-4 py-3">
        <a href={`mailto:${signup.email}`} className={emailClass}>{signup.email}</a>
      </td>
      <td className="px-4 py-3">
        {isUnsub ? (
          <span
            data-testid="admin-status-unsubscribed"
            className="inline-block px-2 py-1 bg-zinc-200 text-zinc-600 text-xs font-semibold uppercase tracking-wider"
            title={`Unsubscribed ${formatDate(signup.unsubscribed_at)}`}
          >
            Unsubscribed
          </span>
        ) : (
          <span
            data-testid="admin-status-subscribed"
            className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold uppercase tracking-wider"
          >
            Subscribed
          </span>
        )}
        {signup.notified_at && !isUnsub && (
          <span
            data-testid="admin-status-notified"
            className="inline-block px-2 py-1 bg-[#F0A500]/15 text-[#B97400] text-xs font-semibold uppercase tracking-wider ml-2"
            title={`Ticket-launch email sent ${formatDate(signup.notified_at)}`}
          >
            ✓ Emailed
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-[#52525B]">{formatDate(signup.submitted_at)}</td>
    </tr>
  );
}

export default function AdminBonfireSignupsPage() {
  const { token, me, checking, loginSuccess, logout } = useAdminAuth();

  if (checking) {
    return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  }

  if (!token || !me) {
    return (
      <div data-testid="admin-bonfire-page">
        <SEO title="Admin Sign-in" description="Restricted admin area." path="/admin/bonfire-signups" noIndex />
        <AdminLoginGate
          title="Admin Sign-in"
          subtitle="Sign in to view the Bonfire Night 2026 launch list."
          onLoggedIn={loginSuccess}
        />
      </div>
    );
  }

  return (
    <div data-testid="admin-bonfire-page">
      <SEO title="Admin — Bonfire Launch List" description="Restricted admin area." path="/admin/bonfire-signups" noIndex />
      <AdminTabs current="bonfire" me={me} onLogout={logout} />
      <SignupsDashboard token={token} onLogout={logout} me={me} />
    </div>
  );
}
